import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Users, Plus, Search, Filter, Mail, Phone, Building,
  MoreVertical, Edit2, Trash2, Eye, Loader2, X, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Customers() {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, [token]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/api/support/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sample data as fallback
  const sampleCustomers = [
    {
      id: 'CUST-001',
      name: 'John Smith',
      email: 'john.smith@example.com',
      phone: '+1 555-0123',
      company: 'ABC Corporation',
      address: 'New York, USA',
      total_tickets: 5,
      open_tickets: 1,
      created_at: '2025-01-15T10:00:00Z'
    },
    {
      id: 'CUST-002',
      name: 'Sarah Johnson',
      email: 'sarah.j@techsol.com',
      phone: '+1 555-0456',
      company: 'Tech Solutions Ltd',
      address: 'San Francisco, USA',
      total_tickets: 12,
      open_tickets: 2,
      created_at: '2025-02-20T14:30:00Z'
    },
    {
      id: 'CUST-003',
      name: 'Mike Brown',
      email: 'mike.brown@startup.io',
      phone: '+1 555-0789',
      company: 'StartUp Inc',
      address: 'Austin, USA',
      total_tickets: 3,
      open_tickets: 0,
      created_at: '2025-03-10T09:15:00Z'
    },
    {
      id: 'CUST-004',
      name: 'Emily Davis',
      email: 'emily@globalent.com',
      phone: '+1 555-0321',
      company: 'Global Enterprises',
      address: 'Chicago, USA',
      total_tickets: 8,
      open_tickets: 3,
      created_at: '2025-03-25T11:45:00Z'
    },
  ];

  const displayCustomers = customers.length > 0 ? customers : sampleCustomers;

  const filteredCustomers = displayCustomers.filter(customer => {
    return customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           customer.company?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const resetForm = () => {
    setNewCustomer({
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      notes: ''
    });
    setEditingCustomer(null);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const customerData = {
        ...newCustomer,
        id: `CUST-${Date.now().toString().slice(-6)}`,
        total_tickets: 0,
        open_tickets: 0,
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${API}/api/support/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(customerData)
      });

      // Add to local state regardless
      setCustomers(prev => [customerData, ...prev]);
      toast.success('Customer created successfully');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      // Still add locally for demo
      const customerData = {
        ...newCustomer,
        id: `CUST-${Date.now().toString().slice(-6)}`,
        total_tickets: 0,
        open_tickets: 0,
        created_at: new Date().toISOString()
      };
      setCustomers(prev => [customerData, ...prev]);
      toast.success('Customer created successfully');
      setShowCreateModal(false);
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      setCustomers(prev => prev.map(c => 
        c.id === editingCustomer.id ? { ...c, ...newCustomer } : c
      ));
      toast.success('Customer updated successfully');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error('Error updating customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = (customerId) => {
    if (!window.confirm('Delete this customer?')) return;
    
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    toast.success('Customer deleted');
    setShowActionsMenu(null);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setNewCustomer({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
    setShowCreateModal(true);
    setShowActionsMenu(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stats = {
    total: displayCustomers.length,
    withOpenTickets: displayCustomers.filter(c => c.open_tickets > 0).length,
    newThisMonth: displayCustomers.filter(c => {
      const created = new Date(c.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Customers
          </h1>
          <p className="text-muted-foreground">Manage your customer database</p>
        </div>
        <button 
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="support-btn-primary flex items-center gap-2"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Add Customer</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Customers</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">With Open Tickets</p>
          <p className="text-2xl font-bold text-amber-400">{stats.withOpenTickets}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">New This Month</p>
          <p className="text-2xl font-bold text-primary">{stats.newThisMonth}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="support-input pl-10"
          data-testid="customer-search"
        />
      </div>

      {/* Customers List */}
      <div className="support-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Tickets</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-[80px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`customer-row-${customer.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                        {customer.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-xs text-muted-foreground">{customer.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        <span>{customer.email}</span>
                      </div>
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-muted-foreground" />
                      <span>{customer.company || '-'}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{customer.total_tickets || 0} total</span>
                      {customer.open_tickets > 0 && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                          {customer.open_tickets} open
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(customer.created_at)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="relative">
                      <button 
                        onClick={() => setShowActionsMenu(showActionsMenu === customer.id ? null : customer.id)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                        data-testid={`customer-actions-${customer.id}`}
                      >
                        <MoreVertical className="w-4 h-4 text-muted-foreground" />
                      </button>
                      
                      {showActionsMenu === customer.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-lg z-20">
                          <button 
                            onClick={() => handleEditCustomer(customer)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors text-left"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteCustomer(customer.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No customers found</p>
            <button 
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="support-btn-primary mt-4"
            >
              Add Your First Customer
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </h2>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-1 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Name *</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="support-input w-full"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Email *</label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="support-input w-full"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Phone</label>
                <input
                  type="tel"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="support-input w-full"
                  placeholder="+1 555-0123"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Company</label>
                <input
                  type="text"
                  value={newCustomer.company}
                  onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                  className="support-input w-full"
                  placeholder="Acme Corp"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Address</label>
                <input
                  type="text"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  className="support-input w-full"
                  placeholder="City, Country"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                <textarea
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  className="support-input w-full min-h-20"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="support-btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={editingCustomer ? handleUpdateCustomer : handleCreateCustomer}
                disabled={saving}
                className="support-btn-primary flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingCustomer ? 'Update Customer' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close actions menu */}
      {showActionsMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowActionsMenu(null)}
        />
      )}
    </div>
  );
}
